import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import { CalendarDays } from 'lucide-react'

export function formatSeasonalDateRange(startsAt, endsAt) {
  if (!startsAt) {
    return null
  }

  const start = dayjs(startsAt)

  if (!start.isValid()) {
    return null
  }

  if (!endsAt) {
    return start.format('MMM D')
  }

  const exclusiveEnd = dayjs(endsAt)

  if (!exclusiveEnd.isValid()) {
    return start.format('MMM D')
  }

  const end = exclusiveEnd.subtract(1, 'day')

  return start.year() === end.year()
    ? `${start.format('MMM D')} – ${end.format('MMM D')}`
    : `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`
}

export default function StorefrontStateBanner({
  action = null,
  activeDesign,
  activeStatus,
  nextStatus,
  skippedStatus,
}) {
  const hasActiveSeasonalDesign = Boolean(activeDesign || activeStatus)
  const activeName = activeStatus?.name ?? activeDesign?.name ?? 'Seasonal'
  const activeRange = formatSeasonalDateRange(activeStatus?.starts_at, activeStatus?.ends_at)
  const skippedRange = formatSeasonalDateRange(skippedStatus?.starts_at, skippedStatus?.ends_at)
  const nextRange = formatSeasonalDateRange(nextStatus?.starts_at, nextStatus?.ends_at)

  let badge = 'Everyday'
  let title = 'Your everyday storefront is showing'
  let schedule = nextStatus
    ? `Next update: ${nextStatus.name}${nextRange ? ` · ${nextRange}` : ''}`
    : null
  let description = nextStatus
    ? 'Customers see your everyday design until the next seasonal update begins.'
    : 'This is what customers see when they visit your store right now.'

  if (hasActiveSeasonalDesign) {
    badge = 'Live now'
    title = `Your ${activeName} storefront is live`
    schedule = activeRange ? `Running ${activeRange}` : 'Activated manually'
    description = 'This is what customers see when they visit your store right now.'
  } else if (skippedStatus) {
    schedule = `${skippedStatus.name} skipped${skippedRange ? ` · was set for ${skippedRange}` : ''}`
    description = `You turned ${skippedStatus.name} off, so customers see your everyday design.`
  }

  return (
    <div className="@container rounded-xl border border-primary/20 bg-primary/[0.04] p-4 sm:p-5">
      <div className="flex min-w-0 flex-col gap-4 @[42rem]:flex-row @[42rem]:items-center @[42rem]:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-interaction">
            <CalendarDays className="size-5" />
          </span>
          <div className="min-w-0">
            <Badge variant={hasActiveSeasonalDesign ? 'success' : 'brand'}>{badge}</Badge>
            <Typography variant="h5" className="mt-2 pb-0">
              {title}
            </Typography>
            {schedule ? (
              <Typography variant="label" className="mt-1 block">
                {schedule}
              </Typography>
            ) : null}
            <Typography variant="supporting" className="mt-1 block">
              {description}
            </Typography>
          </div>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </div>
  )
}
